import { useHasMounted } from "@shared/ui.hooks"
import { clsx } from "clsx"
import React, { useEffect } from "react"
import { createPortal } from "react-dom"

import { DrawerBody } from "./Body"
import * as styles from "./Drawer.css"
import { DrawerFooter } from "./Footer"
import { DrawerHeader } from "./Header"
import { DrawerContextProvider } from "./context"
import { DrawerType } from "./types"

/**
 * A sliding drawer/panel component that appears from any edge of the screen.
 * Follows WAI-ARIA guidelines for modal dialogs with keyboard navigation and screen reader support.
 *
 * @param anchorPosition - Position of the drawer (left, right, top, bottom)
 * @param size - Size of the drawer (default, small, medium, large)
 * @param handleDismiss - Function to call when the drawer should be closed
 * @param isOpen - Controls whether the drawer is visible
 * @param ariaLabel - Accessible label for the drawer (aria-label)
 * @param ariaDescription - Accessible description for the drawer (aria-describedby)
 * @param id - Unique ID for the drawer container
 * @param headerId - ID for the header element (used for accessibility)
 * @param testId - Optional test ID for testing purposes
 * @returns {JSX.Element | null} The drawer component or null if not mounted
 */
export const Drawer: DrawerType = ({
  children,
  anchorPosition = "right",
  size = "default",
  handleDismiss,
  isOpen,
  ariaLabel,
  ariaDescription,
  id = "drawer-container",
  headerId,
  testId,
}) => {
  const mounted = useHasMounted()
  const drawerRef = React.useRef<HTMLDivElement>(null)
  const previousFocusRef = React.useRef<HTMLElement | null>(null)
  const [showTopBorder, setShowTopBorder] = React.useState(false)
  const [showBottomBorder, setShowBottomBorder] = React.useState(false)

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement

      setTimeout(() => {
        if (!drawerRef.current) return
        const focusableElements = drawerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusableElements.length > 0) {
          ;(focusableElements[0] as HTMLElement).focus()
        } else {
          drawerRef.current.focus()
        }
      }, 100)
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (
        target !== target.closest("[role='dialog']") &&
        !target.closest("[role='dialog']") &&
        target.closest("#overlay_portal")
      ) {
        handleDismiss?.()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleDismiss?.()
        return
      }

      if (!isOpen || event.key !== "Tab") return

      if (drawerRef.current) {
        const focusableElements = Array.from(
          drawerRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        )

        if (focusableElements.length === 0) return

        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown)
      document.addEventListener("click", handleClickOutside)
    } else {
      window.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("click", handleClickOutside)
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("click", handleClickOutside)
    }
  }, [isOpen, handleDismiss])

  if (!mounted) return null

  const overlayPortal =
    typeof document !== "undefined" && document.getElementById("overlay_portal")

  if (!overlayPortal) return null

  return createPortal(
    <DrawerContextProvider
      showTopBorder={showTopBorder}
      setShowTopBorder={setShowTopBorder}
      showBottomBorder={showBottomBorder}
      setShowBottomBorder={setShowBottomBorder}
    >
      <div
        data-testid={testId}
        role="presentation"
        className={clsx(
          styles.overlayStyle,
          isOpen ? styles.overlayActiveStyle : styles.overlayInactiveStyle
        )}
        aria-hidden={!isOpen}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleDismiss?.()
          }
        }}
      >
        <div
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={headerId}
          aria-describedby={`${id}-description`}
          id={id}
          data-testid="overlay-container"
          className={`${
            isOpen
              ? styles.animationStyles[anchorPosition].in
              : styles.animationStyles[anchorPosition].out
          } ${styles.drawerStyle[anchorPosition]} ${
            anchorPosition === "top" || anchorPosition === "bottom"
              ? styles.drawerSizeStyle.topBottom[size]
              : styles.drawerSizeStyle.leftRight[size]
          }`}
          tabIndex={-1}
        >
          {ariaDescription && (
            <div id={`${id}-description`} className={styles.screenReaderOnly}>
              {ariaDescription}
            </div>
          )}
          {children}
        </div>
      </div>
    </DrawerContextProvider>,
    overlayPortal
  )
}

Drawer.Header = DrawerHeader
Drawer.Body = DrawerBody
Drawer.Footer = DrawerFooter
